import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/Combobox";
import { SelectWithAdd } from "@/components/QuickAddDialog";
import { fmtBRL } from "@/lib/api";
import { ENTREGADOR_NONE_OPT, RotaBadge } from "./entregasHelpers";
import { formatTelBR, normalizePhoneBR } from "@/lib/phoneUtils";
import { DupCallout } from "@/components/lote/DupCallout";
import { getMissingRequiredFields, isTipoSemFormaObrigatoria } from "./entregasValidation";
import { ClienteAutocomplete, usePedidoLookup } from "@/components/ClienteAutocomplete";
import { WhatsAppFieldStatus } from "@/components/WhatsAppStatusIcon";
import { withRegiao } from "@/lib/bairroOptions";

const PRIORIDADES = [
  { k: "normal", label: "Normal", cls: "bg-neutral-100 text-neutral-700 border-neutral-300", active: "bg-neutral-800 text-white border-neutral-900" },
  { k: "urgente", label: "🚨 Urgente", cls: "bg-red-50 text-red-700 border-red-300", active: "bg-red-600 text-white border-red-700" },
  { k: "vip", label: "⭐ VIP", cls: "bg-purple-50 text-purple-700 border-purple-300", active: "bg-purple-600 text-white border-purple-700" },
];

const TrocoBox = ({ f, isSemTroco }) => {
  if (isSemTroco(f.forma_pgto_id)) {
    return (
      <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
        <div className="text-xs text-slate-600 font-semibold uppercase">Troco</div>
        <div data-testid="sem-troco" className="text-2xl font-extrabold text-slate-500 mono">Sem troco</div>
        <div className="text-xs text-muted-foreground mt-1">Forma de pagamento sem devolução de troco</div>
      </div>
    );
  }
  return (
    <div className="col-span-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
      <div className="text-xs text-orange-700 font-semibold uppercase">Troco a devolver</div>
      <div data-testid="troco-value" className="text-3xl font-extrabold text-orange-700 mono">{fmtBRL(f.troco)}</div>
      <div className="text-xs text-muted-foreground mt-1">Regra: até R$ 50 → troco p/ 50; acima → troco p/ 100 (ajustável)</div>
    </div>
  );
};

/**
 * Formulário Nova/Editar Entrega. Compartilhado entre os 2 dialogs.
 * Props: f (state), target ("form"|"edit"), setField, isColeta, isReceitaRevenda, isSemTroco,
 *         entregadores, bairros, regioes, tipos, formas, canConfigHorario, todayStr, tomorrowStr
 *
 * fev/2026: Validação de campos obrigatórios (bairro/tipo/pedido/cliente/tel/forma).
 * Campos faltando ficam com ring vermelho + asterisco na label. Regra de exceção
 * pra Forma de Pagamento em Coleta/Transferência.
 */
export function EntregasForm({ f, target, setField, isColeta, isReceitaRevenda, isSemTroco,
                              entregadores, bairros, regioes, tipos, formas, rotas = [], canConfigHorario, todayStr, tomorrowStr,
                              user, onCatalogAdd, dup }) {
  const missing = getMissingRequiredFields(f, tipos);
  // Iter 98: rotas ATIVAS que contêm o bairro escolhido. 1 → automática (sem campo);
  // 2+ → campo Rota aparece pro usuário escolher; 0 → entrega fica "Sem rota".
  const rotasDoBairro = useMemo(
    () => (f.bairro_id ? rotas.filter((r) => r.ativa !== false && (r.bairro_ids || []).includes(f.bairro_id)) : []),
    [rotas, f.bairro_id],
  );
  const formaOpcional = isTipoSemFormaObrigatoria(f.tipo_id, tipos);
  const lookupPedido = usePedidoLookup();
  // jun/2026: busca de bairro mostra a região junto (e pesquisa por ela também).
  const bairroOptions = useMemo(() => withRegiao(bairros, regioes), [bairros, regioes]);
  // Classe utilitária pra destacar (ring vermelho) o wrapper de um campo faltando.
  const req = (key, extra = "") => {
    const cls = missing.has(key) ? "ring-2 ring-rose-500 rounded-md ring-offset-2 ring-offset-white" : "";
    return `${cls} ${extra}`.trim();
  };
  // Asterisco vermelho pra label (renderiza só se ainda estiver faltando OU sempre — controlado por `always`)
  const Star = ({ always = true }) => (always ? <span className="text-rose-600 ml-0.5" aria-hidden>*</span> : null);

  // fev/2026: Auto-preenchimento inteligente do cliente ao sair do Nº Pedido.
  // Só preenche se o campo-alvo estiver vazio (não sobrescreve digitação
  // do operador). Bairro/regiao só entram se vazios também.
  const onPedidoBlur = async () => {
    const numero = String(f.numero_pedido || "").trim();
    if (!numero) return;
    await lookupPedido(numero, (found) => {
      if (found.cliente_nome && !String(f.cliente_nome || "").trim()) {
        setField("cliente_nome", found.cliente_nome, target);
      }
      if (found.cliente_telefone && !String(f.cliente_telefone || "").trim()) {
        setField("cliente_telefone", formatTelBR(found.cliente_telefone), target);
      }
      if (found.bairro_id && !String(f.bairro_id || "").trim()) {
        setField("bairro_id", found.bairro_id, target);
      }
    });
  };

  // Normaliza o telefone no blur (insere "9" em celulares antigos)
  const onTelBlur = () => {
    const cur = String(f.cliente_telefone || "").trim();
    if (!cur) return;
    const normDigits = normalizePhoneBR(cur);
    const formatted = formatTelBR(normDigits);
    if (formatted !== cur) {
      setField("cliente_telefone", formatted, target);
    }
  };

  // Callback do autocomplete de Cliente — preenche cliente + telefone + bairro.
  const onClienteFill = (s) => {
    if (s.cliente_nome) setField("cliente_nome", s.cliente_nome, target);
    if (s.cliente_telefone) setField("cliente_telefone", formatTelBR(s.cliente_telefone), target);
    if (s.bairro_id && !String(f.bairro_id || "").trim()) {
      setField("bairro_id", s.bairro_id, target);
    }
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label>Entregador {target === "form" && <span className="text-xs text-muted-foreground">(opcional — pode atribuir depois)</span>}</Label>
        <Combobox
          options={entregadores}
          value={f.entregador_id || "__none__"}
          onChange={(v) => setField("entregador_id", v === "__none__" ? "" : v, target)}
          placeholder="Selecione (opcional)"
          testId={target === "form" ? "select-entregador" : "select-entregador-edit"}
          allowClear={false}
          noneOption={ENTREGADOR_NONE_OPT}
        />
        {target === "form" && f.entregador_id && <p className="text-xs text-emerald-600 mt-1">HR Saída será registrada ao salvar</p>}
        {target === "form" && !f.entregador_id && <p className="text-xs text-amber-600 mt-1">Entrega ficará sem entregador — poderá editar depois</p>}
      </div>
      {target === "form" || target === "edit" ? (
        <div className="col-span-2">
          <Label>Data do lançamento</Label>
          <div className="flex gap-2">
            <Input type="date" value={f.data || ""} onChange={(e) => setField("data", e.target.value, target)}
              onClick={(e) => { try { e.target.showPicker && e.target.showPicker(); } catch (err) { console.debug("showPicker indisponível", err); } }}
              data-testid={target === "form" ? "input-data-lancamento" : "edit-data-lancamento"} className="flex-1" />
            <Button type="button" size="sm" variant="outline" onClick={() => setField("data", todayStr, target)} data-testid={target === "form" ? "btn-data-hoje" : "btn-edit-data-hoje"} className="text-xs">Hoje</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setField("data", tomorrowStr, target)} data-testid={target === "form" ? "btn-data-amanha" : "btn-edit-data-amanha"} className="text-xs border-orange-300 text-orange-700 hover:bg-orange-50">Amanhã</Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {target === "form"
              ? 'Padrão: hoje. Use "Amanhã" para programar para o próximo dia — o próximo usuário verá ao logar.'
              : "Corrija a data se o lançamento foi feito no dia errado. `created_at` é preservado no histórico."}
          </p>
        </div>
      ) : null}
      <div className={req("bairro_id")}>
        <SelectWithAdd
          user={user}
          resource="bairros"
          label="Bairro"
          labelSlot={<>Bairro<Star /></>}
          options={bairroOptions}
          value={f.bairro_id}
          onChange={(v) => setField("bairro_id", v, target)}
          onCatalogAdd={onCatalogAdd}
          afterAdded={(doc) => { if (doc?.regiao_id) setField("regiao_id", doc.regiao_id, target); }}
          useCombobox
          extras={[{ key: "regiao_id", label: "Região", options: regioes, required: true }]}
          testId={target === "form" ? "select-bairro" : "select-bairro-edit"}
        />
      </div>
      <div>
        <SelectWithAdd
          user={user}
          resource="regioes"
          label="Região"
          labelSlot={<>Região <span className="text-xs text-emerald-600">(automática)</span></>}
          options={regioes}
          value={f.regiao_id}
          onChange={(v) => setField("regiao_id", v, target)}
          onCatalogAdd={onCatalogAdd}
          testId={target === "form" ? "select-regiao" : "select-regiao-edit"}
        />
      </div>
      {rotasDoBairro.length > 1 && (
        <div className="col-span-2 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid={`${target}-rota-field`}>
          <Label>Rota <span className="text-xs font-normal text-amber-700">— este bairro está em {rotasDoBairro.length} rotas, escolha uma</span></Label>
          <Select value={f.rota_id || ""} onValueChange={(v) => setField("rota_id", v === "__none__" ? "" : v, target)}>
            <SelectTrigger className="bg-white" data-testid={target === "form" ? "select-rota" : "select-rota-edit"}><SelectValue placeholder="Selecione a rota" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sem rota</SelectItem>
              {rotasDoBairro.map((r) => (
                <SelectItem key={r.id} value={r.id} data-testid={`${target}-rota-opt-${r.id}`}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: r.cor }} />{r.nome}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {rotasDoBairro.length === 1 && (
        <div className="col-span-2 flex items-center gap-2 text-xs text-muted-foreground" data-testid={`${target}-rota-auto`}>
          Rota <span className="text-emerald-600">(automática pelo bairro)</span>: <RotaBadge rota={rotasDoBairro[0]} />
        </div>
      )}
      <div className={req("tipo_id")}>
        <SelectWithAdd
          user={user}
          resource="tipos_entrega"
          endpoint="tipos-entrega"
          label="Tipo de Entrega"
          labelSlot={<>Tipo de Entrega<Star /></>}
          options={tipos}
          value={f.tipo_id}
          onChange={(v) => setField("tipo_id", v, target)}
          onCatalogAdd={onCatalogAdd}
          testId={target === "form" ? "select-tipo" : "select-tipo-edit"}
        />
      </div>
      <div className={req("forma_pgto_id")}>
        <SelectWithAdd
          user={user}
          resource="formas_pgto"
          endpoint="formas-pgto"
          label="Forma de Pagamento"
          labelSlot={<>Forma Pgto{!formaOpcional && <Star />}{formaOpcional && <span className="text-xs text-emerald-600 ml-1">(opcional p/ este tipo)</span>}</>}
          options={formas}
          value={f.forma_pgto_id}
          onChange={(v) => setField("forma_pgto_id", v, target)}
          onCatalogAdd={onCatalogAdd}
          testId={target === "form" ? "select-forma" : "select-forma-edit"}
        />
      </div>
      {isReceitaRevenda(f.tipo_id) ? (
        <>
          <div className={req("numero_pedido_receita")}>
            <Label>Nº Pedido Receita <Star /></Label>
            <Input value={f.numero_pedido_receita || ""} onChange={(e) => setField("numero_pedido_receita", e.target.value, target)} placeholder="Pedido da receita" data-testid={target === "form" ? "input-pedido-receita" : "edit-pedido-receita"} />
          </div>
          <div className={req("numero_pedido_revenda")}>
            <Label>Nº Pedido Revenda <Star /></Label>
            <Input value={f.numero_pedido_revenda || ""} onChange={(e) => setField("numero_pedido_revenda", e.target.value, target)} placeholder="Pedido da revenda" data-testid={target === "form" ? "input-pedido-revenda" : "edit-pedido-revenda"} />
          </div>
          <div className="col-span-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
            <b>Receita + Revenda:</b> ambos os números são obrigatórios pois esta entrega vincula dois pedidos distintos.
          </div>
          <div className="col-span-2">
            <DupCallout dup={dup} testId={target === "form" ? "form-dup-msg" : "edit-dup-msg"} />
          </div>
        </>
      ) : (
        <>
          <div>
            <Label>Quantidade de Pedidos</Label>
            <Input
              type="number"
              min="1"
              max="20"
              value={f.qtd_pedidos || 1}
              onChange={(e) => {
                const q = Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1));
                // Sincroniza pedidos_multiplos com o novo tamanho. Semântica nova
                // (fev/2026): a lista contém TODOS os N números (não só os extras).
                // numero_pedido é sincronizado com pedidos_multiplos[0] pra manter
                // compatibilidade com o resto do sistema (relatórios, tabela etc).
                const atual = Array.isArray(f.pedidos_multiplos) ? f.pedidos_multiplos : [];
                let novoArr;
                if (q === 1) {
                  // Volta pro modo single: descarta a lista, mantém só numero_pedido
                  novoArr = [];
                } else if (q > atual.length) {
                  // Cresceu: aproveita numero_pedido como pedido 1 (se lista estava vazia)
                  const base = atual.length === 0 && f.numero_pedido ? [f.numero_pedido] : atual;
                  novoArr = [...base, ...Array(q - base.length).fill("")];
                } else {
                  novoArr = atual.slice(0, q);
                }
                setField("qtd_pedidos", q, target);
                setField("pedidos_multiplos", novoArr, target);
                // Sincroniza numero_pedido com o primeiro item quando qtd>1
                if (q > 1) setField("numero_pedido", novoArr[0] || "", target);
              }}
              data-testid={target === "form" ? "input-qtd-pedidos" : "edit-qtd-pedidos"}
            />
            <p className="text-xs text-muted-foreground mt-1">Padrão 1. Aumente para digitar N números do mesmo cliente numa única entrega.</p>
          </div>
          {(f.qtd_pedidos || 1) === 1 ? (
            <div className={req("numero_pedido")}>
              <Label>Nº do Pedido <Star /></Label>
              <Input
                value={f.numero_pedido || ""}
                onChange={(e) => setField("numero_pedido", e.target.value, target)}
                onBlur={onPedidoBlur}
                data-testid={target === "form" ? "input-numero-pedido" : "edit-numero-pedido"}
              />
              <DupCallout dup={dup} testId={target === "form" ? "form-dup-msg" : "edit-dup-msg"} />
            </div>
          ) : (
            <div className={`col-span-2 rounded-lg border border-blue-200 bg-blue-50 p-3 ${missing.has("pedidos_multiplos") ? "ring-2 ring-rose-500 ring-offset-2 ring-offset-white" : ""}`} data-testid={target === "form" ? "box-pedidos-multi" : "edit-box-pedidos-multi"}>
              <div className="text-xs uppercase text-blue-800 font-bold tracking-wide mb-1">
                Números dos {f.qtd_pedidos} pedidos desta entrega <Star />
              </div>
              <p className="text-xs text-blue-700/80 mb-2">
                Todos serão salvos numa <b>única entrega</b>. No dashboard/relatórios conta como 1 corrida.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {Array.from({ length: f.qtd_pedidos || 1 }, (_, i) => {
                  const val = (f.pedidos_multiplos || [])[i] || "";
                  // key={i} intencional: slots posicionais fixos (Pedido 1..N),
                  // reordenar não faz sentido — index === identidade estável.
                  return (
                    <div key={`pedido-slot-${i}`}>
                      <Label className="text-xs">Nº do Pedido {i + 1}</Label>
                      <Input
                        value={val}
                        onChange={(e) => {
                          const cur = Array.isArray(f.pedidos_multiplos) ? [...f.pedidos_multiplos] : [];
                          while (cur.length < (f.qtd_pedidos || 1)) cur.push("");
                          cur[i] = e.target.value;
                          setField("pedidos_multiplos", cur, target);
                          // Espelha o primeiro item em numero_pedido pra tabela/relatórios
                          if (i === 0) setField("numero_pedido", e.target.value, target);
                        }}
                        placeholder={`Pedido ${i + 1}`}
                        data-testid={target === "form" ? `input-pedido-${i}` : `edit-pedido-${i}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      <div className={req("cliente_nome")}>
        <Label>Cliente <Star /></Label>
        <ClienteAutocomplete
          value={f.cliente_nome || ""}
          onChange={(v) => setField("cliente_nome", v, target)}
          onFill={onClienteFill}
          placeholder="Nome"
          testId={target === "form" ? "input-cliente-nome" : "edit-cliente-nome"}
        />
      </div>
      <div className={req("cliente_telefone")}>
        <Label>Telefone WhatsApp <Star /></Label>
        <div className="relative">
          <Input
            value={f.cliente_telefone || ""}
            onChange={(e) => setField("cliente_telefone", formatTelBR(e.target.value), target)}
            onBlur={onTelBlur}
            placeholder="(00) 00000-0000"
            data-testid={target === "form" ? "input-cliente-tel" : "edit-cliente-tel"}
            className="pr-9"
          />
          <WhatsAppFieldStatus
            phone={f.cliente_telefone}
            className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
            testId={target === "form" ? "wa-status-form" : "wa-status-edit"}
            showHint={false}
          />
        </div>
        <WhatsAppFieldStatus phone={f.cliente_telefone} testId={target === "form" ? "wa-hint-form" : "wa-hint-edit"} />
      </div>
      <div className="col-span-2">
        <Label>Prioridade</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {PRIORIDADES.map((p) => {
            const isActive = (f.prioridade || "normal") === p.k;
            return (
              <button type="button" key={p.k}
                onClick={() => setField("prioridade", p.k, target)}
                className={`h-9 rounded-md border-2 text-xs font-bold transition ${isActive ? p.active : p.cls}`}
                data-testid={`${target}-prio-${p.k}`}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="col-span-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer bg-orange-50/40 border border-orange-100 rounded px-3 py-2">
          <input type="checkbox" checked={!!f.buscar_receita} onChange={(e) => setField("buscar_receita", e.target.checked, target)} className="accent-orange-600 w-4 h-4" data-testid={target === "form" ? "input-buscar-receita" : "edit-buscar-receita"} />
          <span>Buscar receita no cliente</span>
        </label>
      </div>
      {isColeta(f.tipo_id) ? (
        <div className="col-span-2 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-800">
          <b>Coleta</b>: valor e troco não são necessários para este tipo de entrega
        </div>
      ) : (
        <>
          <div><Label>Valor (R$)</Label><Input data-testid="input-valor" type="number" step="0.01" value={f.valor} onChange={(e) => setField("valor", e.target.value, target)} placeholder="0,00" /></div>
          {!isSemTroco(f.forma_pgto_id) && (
            <>
              <div><Label>Valor Recebido</Label><Input type="number" step="0.01" value={f.valor_recebido} onChange={(e) => setField("valor_recebido", e.target.value, target)} placeholder="0,00" /></div>
              <div><Label>Troco Para (R$)</Label><Input data-testid="input-troco-para" type="number" step="0.01" value={f.troco_para} onChange={(e) => setField("troco_para", e.target.value, target)} placeholder="Automático" /></div>
            </>
          )}
          <TrocoBox f={f} isSemTroco={isSemTroco} />
        </>
      )}
      <div className="col-span-2">
        <Label>Observação <span className="text-muted-foreground font-normal">(opcional — fica visível no detalhe e no acerto)</span></Label>
        <Textarea value={f.observacao || ""} onChange={(e) => setField("observacao", e.target.value, target)} placeholder="Ex.: cliente pediu para entregar na portaria" rows={2} maxLength={500}
          data-testid={target === "form" ? "input-observacao" : "edit-observacao"} />
      </div>
      <div className="col-span-2">
        <Label>Motivo (se remarcar/cancelar)</Label>
        <Textarea value={f.motivo || ""} onChange={(e) => setField("motivo", e.target.value, target)} placeholder="Opcional" />
      </div>
      {target === "edit" && canConfigHorario && (
        <>
          <div>
            <Label>HR Saída (ISO)</Label>
            <div className="flex gap-1">
              <Input value={f.hr_saida || ""} onChange={(e) => setField("hr_saida", e.target.value, target)} placeholder="—" className="text-xs mono" data-testid="edit-hr-saida" />
              <Button type="button" size="sm" variant="outline" onClick={() => setField("hr_saida", new Date().toISOString(), target)} data-testid="btn-hr-saida-agora" className="text-xs whitespace-nowrap">Agora</Button>
            </div>
          </div>
          <div>
            <Label>HR Chegada (ISO)</Label>
            <div className="flex gap-1">
              <Input value={f.hr_chegada || ""} onChange={(e) => setField("hr_chegada", e.target.value, target)} placeholder="—" className="text-xs mono" data-testid="edit-hr-chegada" />
              <Button type="button" size="sm" variant="outline" onClick={() => setField("hr_chegada", new Date().toISOString(), target)} data-testid="btn-hr-chegada-agora" className="text-xs whitespace-nowrap">Agora</Button>
            </div>
          </div>
          <div className="col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            <b>Atenção:</b> alterar HR Saída/Chegada requer confirmação com sua senha.
          </div>
        </>
      )}
      {target === "edit" && !canConfigHorario && (
        <div className="col-span-2 text-xs text-muted-foreground bg-slate-50 border border-slate-200 rounded p-2">
          🔒 Para editar HR Saída ou HR Chegada, seu usuário precisa da permissão <b>Configurar Horário</b>.
        </div>
      )}
    </div>
  );
}
